import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  AlertTriangle,
  CheckCircle2,
  BarChart3,
  Clock,
  Filter
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cn } from '../../lib/utils';
import TimeRangeFilter from '../TimeRangeFilter';

const FeedbackHeader = ({
  isStatsExpanded,
  onToggleStats,
  searchQuery,
  onSearchChange,
  onRefresh,
  onCreateSampleData,
  statusFilter,
  onStatusFilterChange,
  sortBy,
  onSortByChange,
  isLoading,
  stats = {
    total: 156,
    critical: 3,
    resolved: 142,
    satisfaction: 87,
    responseRate: 85
  },
  isMobile
}) => {
  return (
    <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
      <div className="px-6 py-4">
        {/* Top Row: Search and Actions */}
        <div className="flex items-center gap-3 mb-4">
          {/* Search Bar */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search feedback..."
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="pl-9 h-9 text-sm bg-background border-border focus:border-primary/50 focus:ring-1 focus:ring-primary/20 transition-all duration-200"
            />
          </div>

          {/* Filters - Hide on mobile */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              <TimeRangeFilter isMobile={false} />

              {/* Status Filter */}
              {onStatusFilterChange && (
                <Select value={statusFilter} onValueChange={onStatusFilterChange}>
                  <SelectTrigger className="w-32 h-9 bg-background border-border">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="assigned">Assigned</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="wont_fix">Won't Fix</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Sort By */}
              {onSortByChange && (
                <Select value={sortBy} onValueChange={onSortByChange}>
                  <SelectTrigger className="w-32 h-9 bg-background border-border">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="date">Date</SelectItem>
                    <SelectItem value="status">Status</SelectItem>
                    <SelectItem value="priority">Priority</SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Actions */}
              <div className="flex items-center gap-2">
                {onCreateSampleData && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onCreateSampleData}
                    disabled={isLoading}
                    className="h-9 px-3 border-border hover:bg-accent transition-colors"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span className="ml-2 text-sm">Sample Data</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onRefresh}
                  disabled={isLoading}
                  className="h-9 px-3 border-border hover:bg-accent transition-colors"
                >
                  <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                  <span className="ml-2 text-sm">Refresh</span>
                </Button>
              </div>
            </div>
          )}

          {/* Mobile Actions - Simplified */}
          {isMobile && (
            <div className="flex items-center gap-2">
              <TimeRangeFilter isMobile={true} />
              <Button
                variant="outline"
                size="sm"
                onClick={onRefresh}
                disabled={isLoading}
                className="h-9 w-9 p-0 border-border hover:bg-accent transition-colors"
              >
                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
              </Button>
            </div>
          )}
        </div>

        {/* Stats Section */}
        <AnimatePresence>
          {(!isMobile || isStatsExpanded) && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                  title="Total Feedback"
                  value={stats.total}
                  icon={MessageSquare}
                  color="text-blue-600 dark:text-blue-400"
                  bgColor="bg-blue-50 dark:bg-blue-950/30"
                  isMobile={isMobile}
                />
                <StatCard
                  title="Critical"
                  value={stats.critical}
                  icon={AlertTriangle}
                  color="text-amber-600 dark:text-amber-400"
                  bgColor="bg-amber-50 dark:bg-amber-950/30"
                  isMobile={isMobile}
                />
                <StatCard
                  title="Resolved"
                  value={stats.resolved}
                  icon={CheckCircle2}
                  color="text-emerald-600 dark:text-emerald-400"
                  bgColor="bg-emerald-50 dark:bg-emerald-950/30"
                  isMobile={isMobile}
                />
                <StatCard
                  title="Satisfaction"
                  value={`${stats.satisfaction}%`}
                  icon={BarChart3}
                  color="text-purple-600 dark:text-purple-400"
                  bgColor="bg-purple-50 dark:bg-purple-950/30"
                  isMobile={isMobile}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isMobile && (
          <div className="flex justify-end mt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggleStats}
              className="h-8 w-8 p-0 hover:bg-accent transition-colors"
            >
              {isStatsExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color, bgColor, isMobile }) => {
  return (
    <div className={cn(
      "p-4 rounded-lg bg-card border",
      "hover:bg-accent/50 transition-colors duration-200",
      "group"
    )}>
      <div className={cn(
        "flex items-center gap-3",
        isMobile && "flex-col gap-2 text-center"
      )}>
        {!isMobile && (
          <div className={cn(
            "h-8 w-8 rounded-md flex items-center justify-center",
            bgColor,
            "group-hover:scale-105 transition-transform duration-200"
          )}>
            <Icon className={cn("h-4 w-4", color)} />
          </div>
        )}
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">{title}</p>
          <p className="text-lg font-semibold text-foreground">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default FeedbackHeader;